import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Avatar,
  Stack,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import axios from '../../../utils/axios';
import Logo from '../../../components/Logo';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { enqueueSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);

  const totalUnRead = notifications?.filter((item) => item?.status === 0).length;

  const [open, setOpen] = useState(null);
  const AccessToken = window.localStorage.getItem('accessToken');

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  useEffect(() => {
    axios.get(`/api/system/notification/user/${AccessToken}/request/user`).then(data => {
      setNotifications(data.data.notif);
    });
  }, [AccessToken]);

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = async () => {
    await axios(`/api/clear/notification/clear/all/${AccessToken}/by/user`).then(() => {
      setNotifications(
        notifications?.map((notification) => ({
          ...notification,
          status: 1,
        }))
      );
      enqueueSnackbar('Marked Read Successfully');
    });
  };

  return (
    <>
      <IconButtonAnimate 
        color={open ? 'primary' : 'default'} 
        onClick={handleOpen} 
        sx={{ 
          width: 40, 
          height: 40,
          ...(totalUnRead > 0 && {
            '& .MuiBadge-badge': {
              animation: 'pulse 2s infinite ease-in-out',
            },
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(255, 72, 66, 0.4)' },
              '70%': { boxShadow: '0 0 0 10px rgba(255, 72, 66, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(255, 72, 66, 0)' },
            },
            // Glowing effect for the icon itself
            '& svg': {
              filter: (theme) => `drop-shadow(0 0 4px ${alpha(theme.palette.error.main, 0.6)})`,
              animation: 'shake 0.5s infinite alternate ease-in-out',
            },
            '@keyframes shake': {
              '0%': { transform: 'rotate(-10deg)' },
              '100%': { transform: 'rotate(10deg)' },
            },
          })
        }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} height={24} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalUnRead > 0 ? `You have ${totalUnRead} unread messages` : 'No new notifications'}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButtonAnimate color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ maxHeight: 400 }}>
          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1.2, px: 2.5, typography: 'overline', bgcolor: 'background.neutral' }}>
                Latest Notifications
              </ListSubheader>
            }
          >
            {(Array.isArray(notifications) ? notifications : []).slice(0, 10).map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button 
            fullWidth 
            disableRipple
            component={RouterLink}
            to={PATH_DASHBOARD.general.notif}
            onClick={handleClose}
            sx={{ fontWeight: 'bold' }}
          >
            View All Notifications
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.object,
};

function NotificationItem({ notification }) {
  const isUnread = notification?.status === 0;

  return (
    <ListItemButton
      sx={{
        py: 2,
        px: 2.5,
        mt: '1px',
        transition: (theme) => theme.transitions.create('background-color'),
        ...(isUnread && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
        }),
      }}
    >
      <ListItemAvatar sx={{ mt: 0 }}>
        <Box 
          sx={{ 
            p: 0.5, 
            borderRadius: '50%', 
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Logo disabledLink sx={{ width: 32, height: 32 }} />
        </Box>
      </ListItemAvatar>
      
      <ListItemText
        primary={
          <Typography variant="subtitle2" sx={{ fontWeight: isUnread ? 'bold' : 'medium' }}>
            System Message
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 'normal', display: 'block' }}>
              {noCase(notification?.message)}
            </Typography>
          </Typography>
        }
        secondary={
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: 'text.disabled' }}>
            <Iconify icon="eva:clock-outline" sx={{ width: 14, height: 14 }} />
            <Typography variant="caption">{notification?.date}</Typography>
          </Stack>
        }
      />
      
      {isUnread && (
        <Box 
          sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: 'primary.main',
            ml: 1
          }} 
        />
      )}
    </ListItemButton>
  );
}
